import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductCompareActions from 'actions/ProductCompareActions';
import Empty from 'constants/empty';
import { preferredStoreIdSelector } from 'selectors/user/preferredStoreIdSelector';
import zipCodeSelector from 'selectors/user/preferredZipCodeSelector';

const isLocationUnset = value => !value || value === Empty.String;

const { preferredZipCodeSelector } = zipCodeSelector;

/**
 * Hook to fetch product comparison data when modal opens
 * Handles SKU extraction, validation, and auto-close when all products are removed.
 * Refetches when preferred store or zip code changes while the modal is open.
 *
 * @param {boolean} isOpen - Whether the comparison modal is open
 * @param {Array} selectedProducts - Array of selected products to compare
 * @param {Function} onDismiss - Callback function to dismiss the modal
 *
 * @example
 * useComparisonFetch(isModalOpen, selectedProducts, dismissModal);
 */

const onFetchComparison = ProductCompareActions.fetchProductComparison;
const onClearComparisonData = ProductCompareActions.clearComparisonData;

export const useComparisonFetch = (isOpen, selectedProducts, onDismiss) => {
    const dispatch = useDispatch();
    const preferredStore = useSelector(preferredStoreIdSelector);
    const preferredZipCode = useSelector(preferredZipCodeSelector);

    // Track previous location values so effect 2 can detect user-driven changes.
    const prevPreferredStoreRef = useRef(preferredStore);
    const prevPreferredZipCodeRef = useRef(preferredZipCode);

    useEffect(() => {
        if (!isOpen) {
            dispatch(onClearComparisonData());
        }
    }, [isOpen, dispatch]);

    // Effect 1: fetch on modal open or when the selected products list changes.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (selectedProducts.length >= 1) {
            const skuIds = selectedProducts.filter(p => p?.currentSku?.skuId).map(p => p.currentSku.skuId);

            if (skuIds.length > 0) {
                dispatch(onFetchComparison({ skuIds })).catch(err => Sephora.logger.verbose?.('compare fetch rejected', err));
            }
        } else {
            onDismiss();
        }
    }, [isOpen, selectedProducts, dispatch]);

    // Effect 2: re-fetch when the user changes preferred store or zip while the modal
    // is open. Refs detect changes between renders; hydration (Empty.String → value)
    // is skipped so the initial fetch from effect 1 is not duplicated.
    useEffect(() => {
        const storeChanged = preferredStore !== prevPreferredStoreRef.current;
        const zipChanged = preferredZipCode !== prevPreferredZipCodeRef.current;
        const storeHydrating = storeChanged && isLocationUnset(prevPreferredStoreRef.current) && !isLocationUnset(preferredStore);
        const zipHydrating = zipChanged && isLocationUnset(prevPreferredZipCodeRef.current) && !isLocationUnset(preferredZipCode);

        prevPreferredStoreRef.current = preferredStore;
        prevPreferredZipCodeRef.current = preferredZipCode;

        const locationChangedByUser = (storeChanged && !storeHydrating) || (zipChanged && !zipHydrating);

        if (!isOpen || !locationChangedByUser) {
            return;
        }

        const skuIds = selectedProducts.filter(p => p?.currentSku?.skuId).map(p => p.currentSku.skuId);

        if (skuIds.length > 0) {
            dispatch(onFetchComparison({ skuIds })).catch(err => Sephora.logger.verbose?.('compare fetch rejected', err));
        }
    }, [preferredStore, preferredZipCode, isOpen, selectedProducts, dispatch]);
};

export default useComparisonFetch;
